import { ColumnTable } from 'arquero'
import { CellInfoGroups } from 'redux/slices'
import { getColumn } from '../arrow/arqueroHelpers'

export interface PercentageCalculationResult {
  percentage: number
  selectedCount: number
  totalCount: number
  displayText: string
  hasZeroCells: boolean
  breakdown?: Array<{
    filterValue: string
    selectedCount: number
    totalCount: number
    percentage: number
  }>
  filterFieldName?: string
}


/**
 * Calculates the percentage of selected cells using Arquero DataFrames (memory-efficient)
 * Uses columnar operations instead of creating object arrays
 * Main percentage is always relative to all cells in the session (including pre-filtered cells)
 * The scope parameter only affects the breakdown table generation
 */
export const calculatePercentageFromDataFrame = (
  selectedCellIds: string[],
  dataFrame: ColumnTable,
  scope: 'entire_session' | 'current_filters',
  cellInfoGroups: CellInfoGroups,
  hiddenCellInfoGroupValues: { [K in keyof CellInfoGroups]: string[] },
  prefilteredCellIdSet: Set<string>,
  selectedCellInfoGroupName?: string
): PercentageCalculationResult => {
  const selectedCount = selectedCellIds.length
  const selectedCellIdSet = new Set(selectedCellIds)

  // Main percentage is ALWAYS relative to all cells in session
  const totalCount = dataFrame.numRows()
  const percentage = totalCount > 0 ? (selectedCount / totalCount) * 100 : 0

  let breakdown: Array<{
    filterValue: string
    selectedCount: number
    totalCount: number
    percentage: number
  }> = []
  let filterFieldName: string | undefined

  // Generate breakdown table based on scope
  if (selectedCellInfoGroupName) {
    const cellInfoGroup = cellInfoGroups[selectedCellInfoGroupName as keyof typeof cellInfoGroups]
    filterFieldName = cellInfoGroup?.label || selectedCellInfoGroupName

    if (scope === 'entire_session') {
      // Use Arquero groupby for efficient columnar aggregation
      const cellIdColumn = getColumn<string>(dataFrame, 'CELL_ID')
      const groupColumn = getColumn<unknown>(dataFrame, selectedCellInfoGroupName)

      if (cellIdColumn.length > 0 && groupColumn.length > 0) {

        // Group by filter value and calculate counts
        const valueToStats = new Map<string, { total: number; selected: number }>()

        for (let i = 0; i < cellIdColumn.length; i += 1) {
          const value = groupColumn[i]
          if (value !== undefined && value !== null && value !== '') {
            const stringValue = String(value)
            if (!valueToStats.has(stringValue)) {
              valueToStats.set(stringValue, { total: 0, selected: 0 })
            }
            const stats = valueToStats.get(stringValue)
            if (stats) {
              stats.total += 1
              if (selectedCellIdSet.has(cellIdColumn[i])) {
                stats.selected += 1
              }
            }
          }
        }

        breakdown = Array.from(valueToStats.entries()).map(([filterValue, stats]) => ({
          filterValue,
          selectedCount: stats.selected,
          totalCount: stats.total,
          percentage: stats.total > 0 ? (stats.selected / stats.total) * 100 : 0
        })).sort((a, b) => b.percentage - a.percentage)
      }
    } else {
      // Filtered scope - need to apply filters
      const cellIdColumn = getColumn<string>(dataFrame, 'CELL_ID')
      const groupColumn = getColumn<unknown>(dataFrame, selectedCellInfoGroupName)

      if (cellIdColumn.length > 0 && groupColumn.length > 0) {

        const valueToStats = new Map<string, { total: number; selected: number }>()

        for (let i = 0; i < cellIdColumn.length; i += 1) {
          const cellId = cellIdColumn[i]

          // Skip prefiltered cells
          if (!prefilteredCellIdSet.has(cellId)) {
            // Check if cell is hidden by current filters
            const hiddenAttributeNames = Object.keys(hiddenCellInfoGroupValues) as (keyof typeof hiddenCellInfoGroupValues)[]
            let isHidden = false

            // Check each attribute for hidden values
            hiddenAttributeNames.forEach((attrName) => {
              const hiddenValues = hiddenCellInfoGroupValues[attrName]
              if (hiddenValues) {
                const attrColumn = getColumn<unknown>(dataFrame, attrName as string)
                if (attrColumn.length > 0) {
                  if (hiddenValues.includes(String(attrColumn[i]))) {
                    isHidden = true
                  }
                }
              }
            })

            if (!isHidden) {
              // Process this cell
              const value = groupColumn[i]
              if (value !== undefined && value !== null && value !== '') {
                const stringValue = String(value)
                if (!valueToStats.has(stringValue)) {
                  valueToStats.set(stringValue, { total: 0, selected: 0 })
                }
                const stats = valueToStats.get(stringValue)
                if (stats) {
                  stats.total += 1
                  if (selectedCellIdSet.has(cellId)) {
                    stats.selected += 1
                  }
                }
              }
            }
          }
        }

        breakdown = Array.from(valueToStats.entries()).map(([filterValue, stats]) => ({
          filterValue,
          selectedCount: stats.selected,
          totalCount: stats.total,
          percentage: stats.total > 0 ? (stats.selected / stats.total) * 100 : 0
        })).sort((a, b) => b.percentage - a.percentage)
      }
    }
  }

  const displayText = `Percentage of cells: ${percentage.toFixed(2)}% (${selectedCount.toLocaleString()} of ${totalCount.toLocaleString()})`

  return {
    percentage,
    selectedCount,
    totalCount,
    displayText,
    hasZeroCells: false,
    breakdown: breakdown.length > 0 ? breakdown : undefined,
    filterFieldName
  }
}
